import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { useHasFinePointer, usePrefersReducedMotion } from '../../hooks/useMedia';
type Variant = 'solid' | 'outline' | 'outlineLight' | 'ghost';
type BaseProps = {
  children: React.ReactNode;
  variant?: Variant;
  className?: string;
  cursorLabel?: string;
  showArrow?: boolean;
};
type Props = BaseProps & ({
  to: string;
  href?: never;
  onClick?: never;
  type?: never;
  disabled?: never;
} | {
  href: string;
  to?: never;
  onClick?: never;
  type?: never;
  disabled?: never;
} | {
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  to?: never;
  href?: never;
});
const styles: Record<Variant, string> = {
  solid: 'bg-brand text-white hover:bg-brand-deep border border-brand',
  outline: 'border border-ink/25 text-ink hover:border-brand hover:text-brand',
  outlineLight: 'border border-white/40 text-white hover:border-white hover:bg-white/10',
  ghost: 'border border-transparent text-ink hover:text-brand'
};

/**
 * Magnetic CTA — subtly pulls toward the pointer on desktop.
 * Renders as a router Link, external anchor or button depending on props.
 */
export function MagneticButton({
  children,
  variant = 'solid',
  className,
  cursorLabel,
  showArrow = false,
  ...rest
}: Props) {
  const ref = useRef<HTMLSpanElement>(null);
  const [offset, setOffset] = useState({
    x: 0,
    y: 0
  });
  const fine = useHasFinePointer();
  const reduced = usePrefersReducedMotion();
  const magnetic = fine && !reduced;
  const handleMove = (event: React.MouseEvent) => {
    if (!magnetic || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = event.clientX - (rect.left + rect.width / 2);
    const y = event.clientY - (rect.top + rect.height / 2);
    setOffset({
      x: x * 0.22,
      y: y * 0.32
    });
  };
  const reset = () => setOffset({
    x: 0,
    y: 0
  });
  const inner = <>
      <span data-id="element-580" data-mp="3kaf3m4mt3xmi">{children}</span>
      {showArrow ? <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-581" data-mp="3kdt3n67zzuqs" /> : null}
    </>;
  const shared = {
    className: `group inline-flex items-center justify-center gap-2 px-7 py-4 text-[0.8rem] font-medium uppercase tracking-[0.16em] transition-colors duration-300 disabled:cursor-not-allowed disabled:opacity-60 ${styles[variant]} ${className ?? ''}`,
    'data-cursor': cursorLabel ? 'label' : 'hover',
    'data-cursor-label': cursorLabel
  };
  return <motion.span ref={ref} className="inline-flex" onMouseMove={handleMove} onMouseLeave={reset} animate={offset} transition={{
    type: 'spring',
    stiffness: 240,
    damping: 18,
    mass: 0.4
  }} data-id="element-582" data-mp="bud6pnx58ypi">
      {'to' in rest && rest.to ? <Link to={rest.to} {...shared} data-id="element-583" data-mp="qt74p5m1dbvg">
          {inner}
        </Link> : 'href' in rest && rest.href ? <a href={rest.href} target="_blank" rel="noreferrer noopener" {...shared} data-id="element-584" data-mp="vjvxqpdd6gwp">
          {inner}
        </a> : <button type={rest.type ?? 'button'} onClick={rest.onClick} disabled={rest.disabled} {...shared} data-id="element-585" data-mp="14wwfh76akwwe">
          {inner}
        </button>}
    </motion.span>;
}